import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Plus, X } from 'lucide-react';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import {
  DEDICACION_LABELS,
  FUENTE_LABELS,
  IDIOMAS_LABELS,
  JORNADA_LABELS,
  NIVEL_IDIOMA_LABELS,
  PERMISO_TRABAJO_LABELS,
  TERNARIO_LABELS,
  type Candidato,
  type Dedicacion,
  type DuplicadoCandidato,
  type Fuente,
  type Jornada,
  type NivelIdioma,
  type PermisoTrabajo,
  type Ternario,
} from '@/lib/candidatos';
import { useCatalogos } from './catalogos';
import { ChipsSeleccionables } from './Chips';

/**
 * Alta y edición de un candidato.
 *
 * Obligatorio: el nombre y una forma de contactar. Un CV sin teléfono ni correo
 * no sirve para nada el día que hace falta llamar, y es mejor enterarse al
 * guardarlo que tres meses después.
 *
 * El aviso de duplicado salta al salir del campo de email o de teléfono, no al
 * guardar: si ya existe la ficha, quien está tecleando quiere saberlo antes de
 * rellenar los otros doce campos.
 */
interface Props {
  candidato?: Candidato;
  onGuardar: (datos: Record<string, unknown>, forzar: boolean) => Promise<void>;
  onCancelar: () => void;
}

interface Estado {
  nombre: string;
  apellidos: string;
  email: string;
  telefono: string;
  localidad: string;
  permisoTrabajo: PermisoTrabajo;
  idiomas: { codigo: string; nivel: NivelIdioma }[];
  puestos: string[];
  zonas: string[];
  experienciaAnios: string;
  experiencia: string;
  formacion: string;
  vehiculo: Ternario;
  carnet: Ternario;
  desde: string;
  dedicacion: Dedicacion;
  jornada: Jornada[];
  pretensionSalarial: string;
  etiquetas: string[];
  fuente: Fuente;
  fuenteDetalle: string;
  notas: string;
}

function inicial(c?: Candidato): Estado {
  return {
    nombre: c?.nombre === 'Sin nombre' ? '' : c?.nombre || '',
    apellidos: c?.apellidos || '',
    email: c?.email || '',
    telefono: c?.telefono || '',
    localidad: c?.localidad || '',
    permisoTrabajo: c?.permisoTrabajo || 'desconocido',
    idiomas: c?.idiomas || [],
    puestos: c?.puestos || [],
    zonas: c?.zonas || [],
    experienciaAnios: c?.experienciaAnios !== undefined ? String(c.experienciaAnios) : '',
    experiencia: c?.experiencia || '',
    formacion: c?.formacion || '',
    vehiculo: c?.vehiculo || 'desconocido',
    carnet: c?.carnet || 'desconocido',
    // El input date quiere «2026-06-01», y guardamos ISO completo.
    desde: c?.disponibilidad?.desde ? c.disponibilidad.desde.slice(0, 10) : '',
    dedicacion: c?.disponibilidad?.dedicacion || 'sin_especificar',
    jornada: c?.disponibilidad?.jornada || [],
    pretensionSalarial: c?.pretensionSalarial || '',
    etiquetas: c?.etiquetas || [],
    fuente: c?.fuente || 'whatsapp',
    fuenteDetalle: c?.fuenteDetalle || '',
    notas: c?.notas || '',
  };
}

function Campo({
  etiqueta,
  children,
  ayuda,
}: {
  etiqueta: string;
  children: React.ReactNode;
  ayuda?: string;
}) {
  return (
    <div>
      <label className="label">{etiqueta}</label>
      {children}
      {ayuda && <p className="text-xs text-sereno-dark/40 mt-1">{ayuda}</p>}
    </div>
  );
}

function Opciones<T extends string>({
  valor,
  opciones,
  onChange,
}: {
  valor: T;
  opciones: Record<string, string>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(opciones).map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id as T)}
          aria-pressed={valor === id}
          className={classNames(
            'h-10 px-3 rounded-lg border text-sm font-medium transition-colors',
            valor === id
              ? 'border-sereno-dark bg-sereno-dark text-white'
              : 'border-gray-200 bg-white text-sereno-dark/70 hover:border-sereno-dark/40',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function FormularioCandidato({ candidato, onGuardar, onCancelar }: Props) {
  const { catalogos } = useCatalogos();
  const [form, setForm] = useState<Estado>(() => inicial(candidato));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicados, setDuplicados] = useState<DuplicadoCandidato[]>([]);
  const [etiquetaNueva, setEtiquetaNueva] = useState('');

  const set = <K extends keyof Estado>(clave: K, valor: Estado[K]) =>
    setForm((f) => ({ ...f, [clave]: valor }));

  const sinContacto = !form.email.trim() && !form.telefono.trim();
  const puedeGuardar = !!form.nombre.trim() && !sinContacto;

  // Aviso de duplicado al soltar el campo, sin bloquear la escritura.
  const comprobarDuplicados = async () => {
    if (!form.email.trim() && !form.telefono.trim()) return setDuplicados([]);
    try {
      setDuplicados(
        await api.buscarDuplicadosCandidato({
          email: form.email.trim() || undefined,
          telefono: form.telefono.trim() || undefined,
          excluirId: candidato?.id,
        }),
      );
    } catch {
      // El aviso es una cortesía: si falla, el servidor vuelve a comprobarlo.
    }
  };

  useEffect(() => {
    setForm(inicial(candidato));
    setDuplicados([]);
  }, [candidato]);

  const idiomasDisponibles = useMemo(
    () => Object.entries(IDIOMAS_LABELS).filter(([c]) => !form.idiomas.some((i) => i.codigo === c)),
    [form.idiomas],
  );

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeGuardar) return;

    setGuardando(true);
    setError(null);
    try {
      const anios = form.experienciaAnios.trim();
      await onGuardar(
        {
          nombre: form.nombre.trim(),
          apellidos: form.apellidos.trim(),
          email: form.email.trim() || undefined,
          telefono: form.telefono.trim() || undefined,
          localidad: form.localidad.trim() || undefined,
          permisoTrabajo: form.permisoTrabajo,
          idiomas: form.idiomas,
          puestos: form.puestos,
          zonas: form.zonas,
          experienciaAnios: anios ? Number(anios) : undefined,
          experiencia: form.experiencia.trim() || undefined,
          formacion: form.formacion.trim() || undefined,
          vehiculo: form.vehiculo,
          carnet: form.carnet,
          disponibilidad: {
            desde: form.desde ? new Date(form.desde).toISOString() : undefined,
            dedicacion: form.dedicacion,
            jornada: form.jornada,
          },
          pretensionSalarial: form.pretensionSalarial.trim() || undefined,
          etiquetas: form.etiquetas,
          fuente: form.fuente,
          fuenteDetalle: form.fuenteDetalle.trim() || undefined,
          notas: form.notas.trim() || undefined,
        },
        duplicados.length > 0,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-5">
      {/* Contacto */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo etiqueta="Nombre *">
          <input
            className="input h-11"
            required
            value={form.nombre}
            onChange={(e) => set('nombre', e.target.value)}
            autoFocus
          />
        </Campo>
        <Campo etiqueta="Apellidos">
          <input
            className="input h-11"
            value={form.apellidos}
            onChange={(e) => set('apellidos', e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Teléfono">
          <input
            className="input h-11"
            type="tel"
            inputMode="tel"
            value={form.telefono}
            onChange={(e) => set('telefono', e.target.value)}
            onBlur={() => void comprobarDuplicados()}
          />
        </Campo>
        <Campo etiqueta="Email">
          <input
            className="input h-11"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            onBlur={() => void comprobarDuplicados()}
          />
        </Campo>
      </div>

      {sinContacto && (
        <p className="text-sm text-amber-700 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          Hace falta un teléfono o un correo: sin ninguno de los dos no se le podrá llamar.
        </p>
      )}

      {duplicados.length > 0 && (
        <div className="card bg-blue-50 border-blue-200 py-3">
          <p className="text-sm text-blue-900 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              Ya hay {duplicados.length === 1 ? 'una ficha' : `${duplicados.length} fichas`} con ese
              contacto:{' '}
              {duplicados.map((d, i) => (
                <span key={d.id}>
                  {i > 0 && ', '}
                  <Link
                    to={`/equipo/candidatos/${d.id}`}
                    className="underline font-medium"
                    onClick={onCancelar}
                  >
                    {[d.nombre, d.apellidos].filter(Boolean).join(' ') || 'Sin nombre'}
                  </Link>
                </span>
              ))}
              . Si guardas, se creará una ficha aparte.
            </span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo etiqueta="Dónde vive">
          <input
            className="input h-11"
            value={form.localidad}
            onChange={(e) => set('localidad', e.target.value)}
            placeholder="Barbate, Vejer…"
          />
        </Campo>
        <Campo etiqueta="Permiso de trabajo">
          <select
            className="input h-11"
            value={form.permisoTrabajo}
            onChange={(e) => set('permisoTrabajo', e.target.value as PermisoTrabajo)}
          >
            {Object.entries(PERMISO_TRABAJO_LABELS).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      {/* Perfil */}
      <Campo etiqueta="Puestos que puede cubrir">
        <ChipsSeleccionables
          opciones={catalogos.puestos}
          seleccionados={form.puestos}
          onChange={(v) => set('puestos', v)}
          vacio="No hay puestos configurados. Añádelos en Configuración."
        />
      </Campo>

      <Campo etiqueta="Zonas donde puede trabajar" ayuda="Sin marcar ninguna, cualquiera.">
        <ChipsSeleccionables
          opciones={catalogos.zonas}
          seleccionados={form.zonas}
          onChange={(v) => set('zonas', v)}
        />
      </Campo>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo etiqueta="Vehículo propio">
          <Opciones valor={form.vehiculo} opciones={TERNARIO_LABELS} onChange={(v) => set('vehiculo', v)} />
        </Campo>
        <Campo etiqueta="Carnet de conducir">
          <Opciones valor={form.carnet} opciones={TERNARIO_LABELS} onChange={(v) => set('carnet', v)} />
        </Campo>
      </div>

      {/* Disponibilidad */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo etiqueta="Disponible desde">
          <input
            className="input h-11"
            type="date"
            value={form.desde}
            onChange={(e) => set('desde', e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Pretensión salarial">
          <input
            className="input h-11"
            value={form.pretensionSalarial}
            onChange={(e) => set('pretensionSalarial', e.target.value)}
            placeholder="1.300 €/mes, 12 €/hora…"
          />
        </Campo>
      </div>

      <Campo etiqueta="Tipo de dedicación">
        <Opciones
          valor={form.dedicacion}
          opciones={DEDICACION_LABELS}
          onChange={(v) => set('dedicacion', v)}
        />
      </Campo>

      <Campo etiqueta="Jornada">
        <ChipsSeleccionables
          opciones={Object.entries(JORNADA_LABELS).map(([id, label]) => ({ id, label }))}
          seleccionados={form.jornada}
          onChange={(v) => set('jornada', v as Jornada[])}
        />
      </Campo>

      {/* Idiomas */}
      <Campo etiqueta="Idiomas">
        <div className="space-y-2">
          {form.idiomas.map((idioma, i) => (
            <div key={idioma.codigo} className="flex items-center gap-2">
              <span className="flex-1 text-sm">{IDIOMAS_LABELS[idioma.codigo] || idioma.codigo}</span>
              <select
                className="input h-10 w-36"
                value={idioma.nivel}
                onChange={(e) => {
                  const copia = [...form.idiomas];
                  copia[i] = { ...idioma, nivel: e.target.value as NivelIdioma };
                  set('idiomas', copia);
                }}
              >
                {Object.entries(NIVEL_IDIOMA_LABELS).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => set('idiomas', form.idiomas.filter((_, j) => j !== i))}
                className="h-10 w-10 rounded-lg hover:bg-red-50 text-sereno-dark/30 hover:text-red-500 flex items-center justify-center"
                aria-label={`Quitar ${IDIOMAS_LABELS[idioma.codigo] || idioma.codigo}`}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          {idiomasDisponibles.length > 0 && (
            <select
              className="input h-10"
              value=""
              onChange={(e) => {
                if (!e.target.value) return;
                set('idiomas', [...form.idiomas, { codigo: e.target.value, nivel: 'medio' }]);
              }}
            >
              <option value="">Añadir idioma…</option>
              {idiomasDisponibles.map(([codigo, label]) => (
                <option key={codigo} value={codigo}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </div>
      </Campo>

      {/* Trayectoria */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo etiqueta="Años de experiencia">
          <input
            className="input h-11"
            type="number"
            min={0}
            max={70}
            value={form.experienciaAnios}
            onChange={(e) => set('experienciaAnios', e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Formación">
          <input
            className="input h-11"
            value={form.formacion}
            onChange={(e) => set('formacion', e.target.value)}
          />
        </Campo>
      </div>

      <Campo etiqueta="Experiencia">
        <textarea
          className="input resize-none"
          rows={4}
          value={form.experiencia}
          onChange={(e) => set('experiencia', e.target.value)}
          placeholder="Dónde ha trabajado y de qué."
        />
      </Campo>

      {/* Origen y etiquetas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo etiqueta="Cómo nos llegó">
          <select
            className="input h-11"
            value={form.fuente}
            onChange={(e) => set('fuente', e.target.value as Fuente)}
          >
            {Object.entries(FUENTE_LABELS).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta="Detalle del origen">
          <input
            className="input h-11"
            value={form.fuenteDetalle}
            onChange={(e) => set('fuenteDetalle', e.target.value)}
            placeholder="Recomendada por Rocío…"
          />
        </Campo>
      </div>

      <Campo etiqueta="Etiquetas">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {form.etiquetas.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sereno-blue/50 text-sereno-dark text-xs font-medium"
            >
              {t}
              <button
                type="button"
                onClick={() => set('etiquetas', form.etiquetas.filter((x) => x !== t))}
                aria-label={`Quitar ${t}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            className="input h-11 flex-1"
            value={etiquetaNueva}
            onChange={(e) => setEtiquetaNueva(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const limpia = etiquetaNueva.trim();
              if (limpia && !form.etiquetas.includes(limpia)) {
                set('etiquetas', [...form.etiquetas, limpia]);
              }
              setEtiquetaNueva('');
            }}
            placeholder="Verano 2027, urgente…"
          />
          <button
            type="button"
            onClick={() => {
              const limpia = etiquetaNueva.trim();
              if (limpia && !form.etiquetas.includes(limpia)) {
                set('etiquetas', [...form.etiquetas, limpia]);
              }
              setEtiquetaNueva('');
            }}
            className="btn-ghost h-11 px-3"
            aria-label="Añadir etiqueta"
          >
            <Plus size={16} />
          </button>
        </div>
      </Campo>

      <Campo etiqueta="Notas internas" ayuda="No se envían nunca al candidato.">
        <textarea
          className="input resize-none"
          rows={3}
          value={form.notas}
          onChange={(e) => set('notas', e.target.value)}
        />
      </Campo>

      {error && (
        <div className="card bg-red-50 border-red-200 text-red-700 text-sm py-3">{error}</div>
      )}

      <div className="flex gap-2 justify-end sticky bottom-0 bg-white pt-3 pb-1 -mx-1 px-1">
        <button type="button" onClick={onCancelar} disabled={guardando} className="btn-ghost h-11 text-sm">
          Cancelar
        </button>
        <button
          type="submit"
          disabled={guardando || !puedeGuardar}
          className="btn-primary h-11 text-sm flex items-center gap-2"
        >
          {guardando && <Loader2 size={16} className="animate-spin" />}
          {candidato ? 'Guardar cambios' : 'Crear candidato'}
        </button>
      </div>
    </form>
  );
}
